import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import {
  approveContentSubmission,
  getPendingContentSubmissions,
  rejectContentSubmission,
  type PendingContentSubmission,
} from "../api/adminContentApi";
import { assetBaseUrl } from "../config/env";

const PAGE_SIZE = 6;
const uploadsBase = String(assetBaseUrl ?? "").replace(/\/+$/, "");
const baseHasUploadsPrefix = uploadsBase.toLowerCase().endsWith("/uploads");

export default function ContentApprovalList() {
  const [items, setItems] = useState<PendingContentSubmission[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    const load = async () => {
      setInitialLoading(true);
      const page = await getPendingContentSubmissions(0, PAGE_SIZE);
      setItems(page.items);
      setTotalCount(page.totalCount);
      setInitialLoading(false);
    };

    load().catch((error) => {
      console.error("pending content load failed", error);
      Swal.fire("Hata", "Gönderim listesi alınamadı.", "error");
      setItems([]);
      setTotalCount(0);
      setInitialLoading(false);
    });
  }, [refreshKey]);

  const loadMore = async () => {
    if (loadingMore || items.length >= totalCount) {
      return;
    }

    setLoadingMore(true);
    try {
      const page = await getPendingContentSubmissions(items.length, PAGE_SIZE);
      setItems((prev) => [...prev, ...page.items]);
      setTotalCount(page.totalCount);
    } catch (error) {
      console.error("pending content load more failed", error);
      Swal.fire("Hata", "Daha fazla kayıt alınamadı.", "error");
    } finally {
      setLoadingMore(false);
    }
  };

  const refresh = () => setRefreshKey((key) => key + 1);

  const handleApprove = async (content: PendingContentSubmission) => {
    const confirm = await Swal.fire({
      title: "İçeriği onayla",
      text: content.title,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Onayla",
      cancelButtonText: "İptal",
      confirmButtonColor: "#16a34a",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    const success = await approveContentSubmission(content.id);
    if (!success) {
      Swal.fire("Hata", "İçerik onaylanamadı.", "error");
      return;
    }

    Swal.fire("Başarılı", "İçerik onaylandı.", "success");
    refresh();
  };

  const handleReject = async (content: PendingContentSubmission) => {
    const result = await Swal.fire({
      title: "İçeriği reddet",
      input: "textarea",
      inputLabel: "Reddetme nedeni (opsiyonel)",
      inputPlaceholder: "Kısa bir açıklama gir",
      showCancelButton: true,
      confirmButtonText: "Reddet",
      cancelButtonText: "Vazgeç",
      confirmButtonColor: "#dc2626",
    });

    if (!result.isConfirmed) {
      return;
    }

    const success = await rejectContentSubmission(content.id, result.value ?? undefined);
    if (!success) {
      Swal.fire("Hata", "İçerik reddedilemedi.", "error");
      return;
    }

    Swal.fire("Gönderim reddedildi", "İlgili kullanıcıya bildirim gönderildi.", "success");
    refresh();
  };

  return (
    <div className="space-y-4">
      <div className="rounded bg-white p-4 shadow">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">Üye Gönderimleri</h2>
            <p className="text-xs text-slate-500">
              Onay bekleyen seri gönderilerini incele, ilk bölümün önizleme olduğu kurala uyduğunu kontrol et ve uygun aksiyonu seç.
            </p>
          </div>
          <button
            type="button"
            onClick={refresh}
            className="rounded border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-100"
          >
            Yenile
          </button>
        </div>
      </div>

      {initialLoading && <div className="rounded bg-white p-6 text-center text-sm text-slate-500 shadow">Gönderimler yükleniyor...</div>}

      {!initialLoading && items.length === 0 && (
        <div className="rounded bg-white p-6 text-center text-sm text-emerald-600 shadow">Onay bekleyen gönderim yok.</div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {items.map((content) => (
          <article key={content.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white shadow-sm">
            {renderCover(content)}
            <div className="flex flex-1 flex-col gap-3 p-4 text-sm text-slate-700">
              <header className="space-y-1">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-slate-900">{content.title}</h3>
                    <p className="text-xs uppercase tracking-wide text-slate-500">
                      {content.type} · {content.category}
                    </p>
                  </div>
                  <span className="text-[11px] text-slate-500">
                    {content.submittedAtUtc ? formatDate(content.submittedAtUtc) : "Tarih yok"}
                  </span>
                </div>
                <p className="text-xs text-slate-500">Yazar: {content.author}</p>
                {content.creator && (
                  <p className="text-xs text-slate-500">
                    Gönderen: <span className="font-semibold">{content.creator.name}</span> ({content.creator.email})
                  </p>
                )}
              </header>

              {content.description && <p className="text-sm text-slate-600 line-clamp-3">{content.description}</p>}

              {content.submissionStatusReason && (
                <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-700">
                  Önceki not: {content.submissionStatusReason}
                </div>
              )}

              {content.parts.length > 0 && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                  <div className="mb-2 text-[11px] uppercase tracking-wide text-slate-500">
                    Bölümler ({content.partCount})
                  </div>
                  <div className="space-y-3">
                    {content.parts.map((part) => (
                      <PartPreviewRow key={part.id} part={part} resolveUrl={resolveImageUrl} />
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => void handleApprove(content)}
                  className="flex-1 rounded-full bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                >
                  Onayla
                </button>
                <button
                  type="button"
                  onClick={() => void handleReject(content)}
                  className="flex-1 rounded-full bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700"
                >
                  Reddet
                </button>
              </div>
            </div>
          </article>
        ))}
      </div>

      {!initialLoading && items.length < totalCount && (
        <div className="flex justify-center">
          <button
            type="button"
            onClick={loadMore}
            disabled={loadingMore}
            className="rounded-full border border-slate-300 px-5 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 disabled:opacity-60"
          >
            {loadingMore ? "Yükleniyor..." : "Daha fazla yükle"}
          </button>
        </div>
      )}
    </div>
  );
}

function PartPreviewRow({
  part,
  resolveUrl,
}: {
  part: import("../api/adminContentApi").PendingContentPartSummary;
  resolveUrl: (path?: string | null) => string | null;
}) {
  const [expanded, setExpanded] = useState(false);
  const images: string[] = (() => {
    if (!part.imageListJson) return [];
    try {
      const parsed = JSON.parse(part.imageListJson);
      return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === "string") : [];
    } catch {
      return [];
    }
  })();

  return (
    <div className="rounded border border-slate-200 bg-white">
      <div className="flex items-center justify-between px-3 py-2">
        <div>
          <div className="text-sm font-semibold text-slate-800">
            #{part.order} · {part.title}
          </div>
          <div className="text-[11px] text-slate-500">
            {part.isFreePreview ? "Ücretsiz önizleme" : "Premium"} · {part.isApproved ? "Onaylı" : "Onay bekliyor"}
            {images.length > 0 && ` · ${images.length} görsel`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`rounded-full px-2 py-1 text-[10px] font-semibold ${
              part.isFreePreview ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
            }`}
          >
            {part.isFreePreview ? "Free" : "Premium"}
          </span>
          {images.length > 0 && (
            <button
              type="button"
              onClick={() => setExpanded((v) => !v)}
              className="rounded border border-slate-200 px-2 py-0.5 text-[11px] text-slate-600 hover:bg-slate-50"
            >
              {expanded ? "Gizle" : "Görseller"}
            </button>
          )}
        </div>
      </div>
      {expanded && images.length > 0 && (
        <div className="grid grid-cols-3 gap-1 border-t border-slate-100 p-2 sm:grid-cols-4">
          {images.map((img, i) => {
            const src = resolveUrl(img);
            if (!src) return null;
            return (
              <a key={i} href={src} target="_blank" rel="noopener noreferrer">
                <img
                  src={src}
                  alt={`${part.title} - ${i + 1}`}
                  className="h-24 w-full rounded object-cover ring-1 ring-slate-200 hover:ring-indigo-400"
                />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}

function renderCover(content: PendingContentSubmission) {
  const cover =
    resolveImageUrl(content.heroImageUrl) ??     resolveImageUrl(content.coverImageUrl) ??     resolveImageUrl(content.tileImageUrl);

  if (!cover) {
    return null;
  }

  return (
    <div className="relative h-40 w-full overflow-hidden rounded-t-2xl">
      <img src={cover} alt={content.title} className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
    </div>
  );
}

function resolveImageUrl(path?: string | null): string | null {
  if (!path) {
    return null;
  }

  const raw = path.trim();
  if (!raw) {
    return null;
  }

  if (/^https?:\/\//i.test(raw)) {
    return raw;
  }

  if (raw.startsWith("/")) {
    if (/^\/uploads\//i.test(raw)) {
      const suffix = raw.replace(/^\/+uploads\/+/i, "");
      if (!uploadsBase) {
        return `/uploads/${suffix}`;
      }
      if (baseHasUploadsPrefix) {return suffix ? `${uploadsBase}/${suffix}` : uploadsBase;
      }
      return `${uploadsBase}/uploads/${suffix}`;}
    return uploadsBase ? `${uploadsBase}${raw}` : raw;
  }

  const normalized = raw.replace(/^\.\/+/, "").replace(/^\/+/, "");
  const withoutUploads = normalized.replace(/^(?:uploads\/)+/i, "");
  const suffix = withoutUploads;

  if (!uploadsBase) {const relative = withoutUploads ? `uploads/${withoutUploads}` : "uploads";
    return `/${relative}`;
  }

  if (baseHasUploadsPrefix) {return suffix ? `${uploadsBase}/${suffix}` : uploadsBase;}

  const relative = withoutUploads ? `uploads/${withoutUploads}` : "uploads";
  return `${uploadsBase}/${relative}`;
}

function formatDate(value?: string | null): string {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }
  return date.toLocaleString("tr-TR", { dateStyle: "medium", timeStyle: "short" });
}
